import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import {
  RefreshCw, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle,
  History, Brain, Scale, Lightbulb, Plus,
} from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const DISTORTION_KEYS = [
  "allOrNothing", "catastrophizing", "overgeneralization", "mindReading",
  "fortuneTelling", "magnification", "emotionalReasoning", "shouldStatements",
  "labeling", "personalization",
];

const CognitiveRestructuringPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [step, setStep] = useState(0);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    situation: "",
    automatic_thought: "",
    emotions: [],
    emotion_intensity_before: 7,
    distortions: [],
    evidence_for: "",
    evidence_against: "",
    alternative_thought: "",
    emotion_intensity_after: 4,
    action_plan: "",
  });
  const [customEmotion, setCustomEmotion] = useState("");

  const EMOTIONS = [
    t("emotion.anxious"), t("emotion.sadness"), t("emotion.anger"),
    t("emotion.fear"), t("emotion.frustrated"), t("emotion.ashamed"),
    t("emotion.guilty"), t("emotion.hopeless"), t("emotion.irritated"),
    t("emotion.worried"), t("emotion.joy"), t("emotion.surprise"),
  ];

  const STEPS = [
    { icon: AlertTriangle, title: t("cogRestructuring.step.situation"), desc: t("cogRestructuring.step.situationDesc") },
    { icon: Brain, title: t("cogRestructuring.step.thought"), desc: t("cogRestructuring.step.thoughtDesc") },
    { icon: AlertTriangle, title: t("cogRestructuring.step.distortions"), desc: t("cogRestructuring.step.distortionsDesc") },
    { icon: Scale, title: t("cogRestructuring.step.evidence"), desc: t("cogRestructuring.step.evidenceDesc") },
    { icon: Lightbulb, title: t("cogRestructuring.step.alternative"), desc: t("cogRestructuring.step.alternativeDesc") },
  ];

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/cognitive-restructuring`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleEmotion = (emotion) => {
    setFormData(prev => ({
      ...prev,
      emotions: prev.emotions.includes(emotion)
        ? prev.emotions.filter(e => e !== emotion)
        : [...prev.emotions, emotion],
    }));
  };

  const addCustomEmotion = () => {
    const trimmed = customEmotion.trim();
    if (trimmed && !formData.emotions.includes(trimmed)) {
      setFormData(prev => ({ ...prev, emotions: [...prev.emotions, trimmed] }));
      setCustomEmotion("");
    }
  };

  const toggleDistortion = (key) => {
    setFormData(prev => ({
      ...prev,
      distortions: prev.distortions.includes(key)
        ? prev.distortions.filter(d => d !== key)
        : [...prev.distortions, key],
    }));
  };

  const handleSubmit = async () => {
    if (!formData.situation || !formData.automatic_thought) {
      toast.error(t("cogRestructuring.fillRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/cognitive-restructuring`, formData, { withCredentials: true });
      toast.success(t("cogRestructuring.saved"));
      if (user?.role === "patient") promptShare(res.data, "cognitive_restructuring");
      setFormData({
        situation: "", automatic_thought: "", emotions: [], emotion_intensity_before: 7,
        distortions: [], evidence_for: "", evidence_against: "",
        alternative_thought: "", emotion_intensity_after: 4, action_plan: "",
      });
      setCustomEmotion("");
      setStep(0);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <Textarea
              placeholder={t("cogRestructuring.situationPlaceholder")}
              value={formData.situation}
              onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
              rows={4}
              data-testid="situation-input"
            />
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("cogRestructuring.step.emotions")}</label>
              <div className="flex flex-wrap gap-2">
                {EMOTIONS.map((em) => (
                  <button
                    key={em}
                    onClick={() => toggleEmotion(em)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      formData.emotions.includes(em)
                        ? "bg-primary text-white border-primary"
                        : "bg-background border-border hover:bg-muted"
                    }`}
                  >
                    {em}
                  </button>
                ))}
                {formData.emotions.filter(e => !EMOTIONS.includes(e)).map(custom => (
                  <button key={custom} onClick={() => toggleEmotion(custom)}
                    className="px-3 py-1.5 rounded-full text-sm border transition-all bg-primary text-white border-primary">
                    {custom}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder={t("thought.customEmotion")} value={customEmotion}
                  onChange={(e) => setCustomEmotion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomEmotion())}
                  className="flex-1 px-3 py-2 text-sm border rounded-full bg-background border-border focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="custom-emotion-input" />
                <Button variant="outline" size="sm" onClick={addCustomEmotion} disabled={!customEmotion.trim()} className="rounded-full" data-testid="add-custom-emotion-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("cogRestructuring.emotionIntensityBefore")}</label>
              <Slider
                value={[formData.emotion_intensity_before]}
                onValueChange={(v) => setFormData({ ...formData, emotion_intensity_before: v[0] })}
                min={1} max={10} step={1}
                data-testid="intensity-before-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t("cogRestructuring.low")}</span>
                <span>{formData.emotion_intensity_before}/10</span>
                <span>{t("cogRestructuring.high")}</span>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <Textarea
              placeholder={t("cogRestructuring.thoughtPlaceholder")}
              value={formData.automatic_thought}
              onChange={(e) => setFormData({ ...formData, automatic_thought: e.target.value })}
              rows={4}
              data-testid="thought-input"
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              {DISTORTION_KEYS.map((key) => (
                <button
                  key={key}
                  onClick={() => toggleDistortion(key)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.distortions.includes(key)
                      ? "bg-primary/10 border-primary ring-1 ring-primary"
                      : "bg-background border-border hover:bg-muted"
                  }`}
                  data-testid={`distortion-${key}`}
                >
                  <p className="font-medium text-sm">{t(`distortion.${key}`)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t(`distortion.${key}.desc`)}</p>
                </button>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-green-700 dark:text-green-400">{t("cogRestructuring.evidenceFor")}</label>
              <Textarea
                placeholder={t("cogRestructuring.evidenceForPlaceholder")}
                value={formData.evidence_for}
                onChange={(e) => setFormData({ ...formData, evidence_for: e.target.value })}
                rows={3}
                data-testid="evidence-for-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-red-700 dark:text-red-400">{t("cogRestructuring.evidenceAgainst")}</label>
              <Textarea
                placeholder={t("cogRestructuring.evidenceAgainstPlaceholder")}
                value={formData.evidence_against}
                onChange={(e) => setFormData({ ...formData, evidence_against: e.target.value })}
                rows={3}
                data-testid="evidence-against-input"
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-5">
            <Textarea
              placeholder={t("cogRestructuring.alternativePlaceholder")}
              value={formData.alternative_thought}
              onChange={(e) => setFormData({ ...formData, alternative_thought: e.target.value })}
              rows={3}
              data-testid="alternative-thought-input"
            />
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("cogRestructuring.emotionIntensityAfter")}</label>
              <Slider
                value={[formData.emotion_intensity_after]}
                onValueChange={(v) => setFormData({ ...formData, emotion_intensity_after: v[0] })}
                min={1} max={10} step={1}
                data-testid="intensity-after-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t("cogRestructuring.low")}</span>
                <span>{formData.emotion_intensity_after}/10</span>
                <span>{t("cogRestructuring.high")}</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("cogRestructuring.actionPlan")}</label>
              <Textarea
                placeholder={t("cogRestructuring.actionPlanPlaceholder")}
                value={formData.action_plan}
                onChange={(e) => setFormData({ ...formData, action_plan: e.target.value })}
                rows={2}
                data-testid="action-plan-input"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="cognitive-restructuring-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("cogRestructuring.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("cogRestructuring.pageSubtitle")}</p>
          </div>
          <Button
            variant={showHistory ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history-btn"
          >
            <History className="w-4 h-4 mr-1" />
            {t("cogRestructuring.history")} ({entries.length})
          </Button>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.cognitive_restructuring} />

        {showHistory ? (
          <div className="space-y-4">
            {entries.length > 0 ? entries.map((entry) => (
              <Card key={entry.entry_id} className="hover:shadow-sm transition-shadow">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{entry.situation}</p>
                      <p className="text-xs text-muted-foreground mt-1 italic">"{entry.automatic_thought}"</p>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                        {entry.emotion_intensity_before}/10
                      </span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">
                        {entry.emotion_intensity_after}/10
                      </span>
                    </div>
                  </div>
                  {entry.distortions?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {entry.distortions.map((d) => (
                        <span key={d} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                          {t(`distortion.${d}`)}
                        </span>
                      ))}
                    </div>
                  )}
                  {entry.alternative_thought && (
                    <p className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/50 p-2 rounded">
                      {entry.alternative_thought}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(entry.created_at))}
                  </p>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("cogRestructuring.noEntries")}</p>
                <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("cogRestructuring.startRestructuring")}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-1.5 max-w-3xl mx-auto">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full cursor-pointer transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`}
                  onClick={() => setStep(i)}
                />
              ))}
            </div>

            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  {(() => { const SIcon = STEPS[step].icon; return <SIcon className="w-5 h-5" />; })()}
                  {STEPS[step].title}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{STEPS[step].desc}</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderStepContent()}

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                  </Button>
                  {step < STEPS.length - 1 ? (
                    <Button onClick={() => setStep(step + 1)} data-testid="next-step-btn">
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="save-restructuring-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {submitting ? t("exercise.saving") : t("common.save")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default CognitiveRestructuringPage;
